import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FlagIcon } from "@heroicons/react/24/outline";
import { Spinner } from "../Spinner";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  candidateCount: number;
  prompt: string | null;
  promptLoading: boolean;
  isInProgress: boolean;
};

export default function FlagCVConfirmModal({
  open,
  onClose,
  onConfirm,
  candidateCount,
  prompt,
  promptLoading,
  isInProgress,
}: Props) {
  const cancelButtonRef = useRef(null);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 dark:bg-opacity-20 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-2xl">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <FlagIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900 flex items-center gap-2"
                      >
                        Flag CVs
                        {isInProgress && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500 animate-pulse" />
                            In Progress
                          </span>
                        )}
                      </Dialog.Title>

                      {isInProgress ? (
                        <div className="mt-3 rounded-md bg-yellow-50 border border-yellow-200 p-4">
                          <div className="flex items-center gap-2">
                            <Spinner height="h-5" width="w-5" />
                            <p className="text-sm text-yellow-800 font-medium">
                              A CV flagging batch is already in progress.
                            </p>
                          </div>
                          <p className="mt-1 text-sm text-yellow-700">
                            Please wait for the current batch to complete before starting a new one. You can safely refresh the page — it will reconnect automatically.
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className="mt-1 text-sm text-gray-500">
                            This will flag CVs for{" "}
                            <span className="font-semibold text-gray-700">
                              {candidateCount} candidate{candidateCount !== 1 ? "s" : ""}
                            </span>{" "}
                            using the following AI prompt. Review the prompt before confirming.
                          </p>

                          <div className="mt-3">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                              Prompt Preview
                            </p>
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 max-h-72 overflow-y-auto">
                              {promptLoading ? (
                                <div className="flex items-center justify-center py-6">
                                  <Spinner height="h-6" width="w-6" />
                                </div>
                              ) : prompt ? (
                                <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono leading-relaxed">
                                  {prompt}
                                </pre>
                              ) : (
                                <p className="text-xs text-gray-400 italic">
                                  Could not load prompt preview.
                                </p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  {!isInProgress && (
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50 sm:ml-3 sm:w-auto"
                      onClick={onConfirm}
                      disabled={promptLoading}
                    >
                      Confirm &amp; Start
                    </button>
                  )}
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                    ref={cancelButtonRef}
                  >
                    {isInProgress ? "Close" : "Cancel"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
